import { useCallback, useEffect, useState } from "react";
import type { RequestState } from "@redux/state/shared-types";

interface UseAITextGenerationOptions {
  processing: RequestState;
  onGenerate: () => Promise<string>;
  onReset: () => void;
}

export const useAITextGeneration = ({ processing, onGenerate, onReset }: UseAITextGenerationOptions) => {
  const [hasGenerated, setHasGenerated] = useState(false);

  const isGenerating = processing === "pending";

  useEffect(() => {
    if (processing === "success") {
      setHasGenerated(true);
      onReset();
    }
    if (processing === "failed") {
      onReset();
    }
  }, [processing, onReset]);

  const handleGenerate = useCallback(async () => {
    if (isGenerating || hasGenerated) return;

    try {
      await onGenerate();
    } catch {
      // Error handling is done reactively via processing state
    }
  }, [isGenerating, hasGenerated, onGenerate]);

  return {
    isGenerating,
    isDisabled: isGenerating || hasGenerated,
    hasGenerated,
    handleGenerate,
  };
};
